import { createApi } from "@reduxjs/toolkit/query/react";
import { HEADER, TagId, TagType } from "../../configs";
import {
  Header,
  HeaderDetails,
  HttpMethod,
  IWriteHeader,
  PaginatedResponse,
  SearchByDisplayName,
} from "../../types";
import { createBaseQuery } from "../helpers/baseQueryHelper";

const headerApi = createApi({
  reducerPath: HEADER.reducerPath,
  baseQuery: createBaseQuery(HEADER.baseUrl),
  tagTypes: HEADER.tagTypes,
  endpoints(builder) {
    return {
      fetchAllHeaders: builder.query<Header[], string>({
        query: (restaurantId) => {
          return {
            url: `/${restaurantId}/restaurant/all`,
            method: HttpMethod.GET,
          };
        },
        providesTags: () => [{ type: TagType.Header, id: TagId.ALL_LIST }],
      }),

      toggleHeaderIsActiveStatus: builder.mutation<
        void,
        { headerId: string; isActive: boolean }
      >({
        query: ({ headerId, isActive }) => ({
          url: `/${headerId}/active`,
          method: HttpMethod.PATCH,
          params: { isActive },
        }),
        invalidatesTags: () => [{ type: TagType.Header, id: TagId.LIST }],
      }),

      fetchHeaders: builder.query<
        PaginatedResponse<Header>,
        { restaurantId: string; searchQuery: SearchByDisplayName }
      >({
        query: ({ restaurantId, searchQuery }) => {
          return {
            url: `/${restaurantId}/restaurant`,
            method: HttpMethod.GET,
            params: searchQuery,
          };
        },
        providesTags: () => [{ type: TagType.Header, id: TagId.LIST }],
      }),

      fetchHeaderDetails: builder.query<HeaderDetails, string>({
        query: (headerId) => {
          return {
            url: `/${headerId}`,
            method: HttpMethod.GET,
          };
        },
        providesTags: (_result, _error, headerId) => [
          { type: TagType.Header, id: headerId },
        ],
      }),

      createHeader: builder.mutation<HeaderDetails, IWriteHeader>({
        query: (body) => ({
          url: "",
          method: HttpMethod.POST,
          body,
        }),
        invalidatesTags: () => [
          { type: TagType.Header, id: TagId.ALL_LIST },
          { type: TagType.Header, id: TagId.LIST },
        ],
      }),

      updateHeader: builder.mutation<
        void,
        { headerId: string; body: IWriteHeader }
      >({
        query: ({ headerId, body }) => ({
          url: `/${headerId}`,
          method: HttpMethod.PUT,
          body,
        }),
        invalidatesTags: (_result, _error, { headerId }) => [
          { type: TagType.Header, id: TagId.ALL_LIST },
          { type: TagType.Header, id: TagId.LIST },
          { type: TagType.Header, id: headerId },
        ],
      }),

      deleteHeader: builder.mutation<void, string>({
        query: (headerId) => ({
          url: `/${headerId}`,
          method: HttpMethod.DELETE,
        }),
        invalidatesTags: (_result, _error, headerId) => [
          { type: TagType.Header, id: TagId.ALL_LIST },
          { type: TagType.Header, id: TagId.LIST },
          { type: TagType.Header, id: headerId },
        ],
      }),
    };
  },
});

export const {
  useFetchAllHeadersQuery,
  useFetchHeadersQuery,
  useFetchHeaderDetailsQuery,
  useToggleHeaderIsActiveStatusMutation,
  useCreateHeaderMutation,
  useUpdateHeaderMutation,
  useDeleteHeaderMutation,
} = headerApi;

export { headerApi };
